// ---------------------------------------------------------------------------
// The browser side of AUTO-MATCH, RANKING AND EXPLAINABILITY. V26.
//
// NO VOCABULARY IS SPELLED IN THIS FILE OR IN THE SCREEN THAT USES IT. A factor
// arrives as an id AND its label AND the sentence a worker reads; an exclusion
// arrives with its label; the acts a human may take arrive as rows from
// `/v1/reference/match-vocabulary`, with `needs_candidate` saying which of them
// need a person named. `verify-V26.sh` §5 reads all three vocabularies out of the
// database and greps this directory for the quoted form of every value.
//
// THE SCREEN COMPUTES NO SCORE AND WRITES NO SENTENCE. Every number and every
// sentence here was produced by `app.rank_candidates` and stored, which is what
// makes what a worker is shown today the same as what a tribunal reads in a year.
// A component that assembled its own summary would be a second explanation, and
// the one nobody checked would be the one on the screen.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type MatchRun = {
  run_id: string;
  ranked_at: string;
  ranked_by: string | null;
  shift_starts_at: string;
  shift_ends_at: string;
  pool_count: number;
  ranked_count: number;
  excluded_count: number;
  override_count: number;
  /** When this record of an automated decision stops being kept. */
  retain_until: string;
};

export type MatchCandidate = {
  candidate_id: string;
  worker_id: string;
  worker_name: string;
  /** Null for somebody the ranking excluded. There is no third shape. */
  rank: number | null;
  score: string | null;
  /** Answer 64: shown and sortable, never scored. */
  travel_minutes?: number | null;
  max_travel_minutes?: number | null;
  factors_measured: number;
  exclusion_reason: string | null;
  exclusion_label: string | null;
  /** Never empty. The database refuses a candidate row without one. */
  explanation: string;
  overridden: boolean;
  override_action: string | null;
};

export type MatchOverride = {
  override_id: string;
  worker_id: string | null;
  worker_name: string | null;
  action_id: string;
  action_label: string;
  reason: string | null;
  decided_by: string | null;
  decided_at: string;
};

export type MatchFactorLine = {
  factor_id: string;
  factor_label: string;
  /**
   * FALSE MEANS THE FACTOR WAS LEFT OUT OF THE SCORE ENTIRELY — not that it
   * scored badly. The distinction is the whole legal point of the slice and the
   * screen must never render an unmeasurable factor as a zero.
   */
  is_measurable: boolean;
  points: string | null;
  weight: string;
  sentence: string;
};

export type MatchVocabulary = {
  match_factors: { id: string; label: string; weight: string; rank: number; description: string }[];
  match_exclusion_reasons: {
    id: string;
    label: string;
    rank: number;
    is_worker_stated: boolean;
    description: string;
  }[];
  match_override_actions: {
    id: string;
    label: string;
    rank: number;
    needs_candidate: boolean;
    description: string;
  }[];
};

export const match = {
  vocabulary: (base: string) => read(base, "/v1/reference/match-vocabulary"),

  runs: (base: string, requirementId: string) =>
    read(base, `/v1/requirements/${encodeURIComponent(requirementId)}/match-runs`),

  run: (base: string, requirementId: string) =>
    mutate("POST", base, `/v1/requirements/${encodeURIComponent(requirementId)}/match-runs`, {}),

  candidates: (base: string, runId: string) =>
    read(base, `/v1/match-runs/${encodeURIComponent(runId)}`),

  breakdown: (base: string, candidateId: string) =>
    read(base, `/v1/match-candidates/${encodeURIComponent(candidateId)}/breakdown`),

  override: (
    base: string,
    runId: string,
    payload: { action: string; worker_id?: string | null; reason?: string | null },
  ) => mutate("POST", base, `/v1/match-runs/${encodeURIComponent(runId)}/overrides`, payload),
};
